import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { CLOSED_ORDER_STATUSES } from '../constants';
import { invalidateQueries } from '../lib/reactQueryHelpers';
import { WsOrder } from '../types/orders';
import { getEditOrderMutationKey, MutationStateSchema } from './useOrderEditMutation';
import { invalidateOrders, OrderEntities } from './useOrders';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { subaccountBalanceQuery } from './useSubaccountBalance';
import { useOrderUpdate } from './useWebsocketStore';

export const useOrdersSub = () => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const queryClient = useQueryClient();
  const currentSubaccountId = selectedSubaccount?.id;

  const handler = useCallback(
    (order: WsOrder) => {
      if (!currentSubaccountId) return;

      // Invalidate subaccount balances to refresh available margin
      invalidateQueries(queryClient, subaccountBalanceQuery(currentSubaccountId).queryKey);

      const isEditingOrder =
        queryClient.isMutating({
          mutationKey: getEditOrderMutationKey({ productId: order.productId }),
          predicate: (mutation) => {
            const result = MutationStateSchema.safeParse(mutation.state);

            if (!result.success) return false;
            if (mutation.state.status !== 'pending') return false;

            return result.data.variables.existingOrderId === order.id;
          },
        }) > 0;

      // When we editing an order, we don't want to invalidate the orders, the `useOrderEditMutation` will invalidate the orders.
      if (isEditingOrder) {
        return;
      }

      if (typeof order.stopType === 'number' && order.close) {
        invalidateOrders(queryClient, currentSubaccountId, OrderEntities.positionStopOrders);
      } else {
        invalidateOrders(queryClient, currentSubaccountId, OrderEntities.openOrders);
        if (CLOSED_ORDER_STATUSES.map(String).includes(order.status)) {
          invalidateOrders(queryClient, currentSubaccountId, OrderEntities.default);
        }
      }
    },
    [currentSubaccountId, queryClient]
  );

  useOrderUpdate(currentSubaccountId, handler);
};
